import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal, SectionHead } from "@/components/Reveal";
import { FaqAccordion } from "@/components/FaqAccordion";
import { AreaCta, AreaSearch, GettingHere } from "@/components/Areas";
import { ContactActions, HoursList, MapFrame } from "@/components/VisitBlock";
import { AREAS, AREAS_BY_STATE, STATES } from "@/lib/areas";
import { BUSINESS } from "@/lib/site";

const CRUMBS = [{ name: "Home", path: "/" }, { name: "Areas We Serve", path: "/areas-we-serve" }];

const STATE_COPY = {
  "washington-dc": "Walkable from U Street, Logan Circle, Shaw and Dupont — and a Green line ride from much of the District. Every DC neighborhood, organized ward by ward.",
  maryland: "Montgomery and Prince George's County patients can register as visitors, order ahead and pick up at our 14th Street shop.",
  virginia: "Arlington, Alexandria and Northern Virginia patients can register as visitors, order ahead and pick up at our 14th Street shop.",
};

const FAQS = [
  { q: "Is there a medical cannabis dispensary near me in Washington, DC?", a: "District Gaslight is an ABCA-licensed medical cannabis dispensary at 1738 14th St NW, Washington, DC 20009 — in the Logan Circle / U Street area, upstairs next to Chicken + Whiskey. We're open daily from 10 AM, until 11 PM on Fridays and Saturdays." },
  { q: "Which neighborhoods are closest to District Gaslight?", a: "U Street, Logan Circle, Shaw, Dupont Circle, Kalorama and Adams Morgan are all within about a mile. Much of the city is a direct Green line ride to U Street/Cardozo, a few blocks from our door." },
  { q: "Can I shop if I live in Maryland or Virginia?", a: "Yes. Visitors can register through ABCA's non-DC resident form at myabca.dc.gov, or bring a valid medical cannabis card from their home state. Order ahead online, then pick up at our 14th Street shop. Cannabis purchased in DC should not be transported across state lines." },
  { q: "Do you deliver?", a: "This site is set up for order-ahead pickup at our 14th Street shop. Call (202) 652-1567 to ask about any other options." },
  { q: "How do I find the entrance?", a: "Look for Chicken + Whiskey at 1738 14th St NW. Take the first door on the left, head up the stairs and ring the doorbell." },
];
const FAQ_LD = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };

const STATS = [[AREAS.length, "Areas"], [3, "States"], ["Daily", "From 10 AM"], ["ABCA", "Licensed"]];

const StateCard = ({ k, i }) => {
  const s = STATES[k];
  const list = AREAS_BY_STATE[k];
  const top = [...list].sort((a, b) => a.mi - b.mi).slice(0, 4);
  return (
    <Reveal delay={0.06 * i} className={`flex h-full flex-col rounded-2xl p-8 ${i === 0 ? "bg-brand-ink text-white" : "border border-neutral-200 bg-white"}`}>
      <p className={i === 0 ? "eyebrow-lime" : "eyebrow"}>{s.label} · {list.length} areas</p>
      <h3 className="mt-3 font-display text-3xl font-black tracking-tight">{s.name}</h3>
      <p className={`mt-3 text-sm leading-relaxed ${i === 0 ? "text-neutral-300" : "text-neutral-600"}`}>{STATE_COPY[k]}</p>
      <p className={`mt-5 text-xs font-semibold ${i === 0 ? "text-neutral-400" : "text-neutral-500"}`}>{top.map((a) => a.name).join(" · ")} + {list.length - top.length} more</p>
      <Link to={`/areas-we-serve/${k}`} data-testid={`state-card-${k}`} className={`mt-auto inline-flex items-center gap-2 pt-6 text-sm font-bold ${i === 0 ? "text-brand-lime" : "text-brand-red"}`}>
        Explore {s.short} areas <ArrowRight className="h-4 w-4" />
      </Link>
    </Reveal>
  );
};

export default function AreasHub() {
  return (
    <>
      <Seo
        title="Dispensary Near Me in DC, MD & VA | Areas We Serve | District Gaslight"
        description={`Find a medical dispensary near you: District Gaslight on 14th St NW serves ${AREAS.length} DC neighborhoods plus Maryland and Virginia visitors with order-ahead pickup.`}
        path="/areas-we-serve"
        crumbs={CRUMBS}
        jsonLd={FAQ_LD}
      />
      <PageHeader crumbs={CRUMBS} eyebrow="Service areas" title="Your dispensary near" highlight="every corner of DC" image="/img/storefront.webp" lead={`Looking for an open dispensary near you? District Gaslight is an ABCA-licensed medical cannabis dispensary on 14th Street NW, serving patients from ${AREAS_BY_STATE["washington-dc"].length} DC neighborhoods and visitors from Maryland and Virginia with order-ahead pickup — open daily from 10 AM.`}>
        <div className="mt-8"><ContactActions where="areas-hero" dark /></div>
        <dl className="mt-10 grid max-w-2xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/15 bg-white/15 sm:grid-cols-4">
          {STATS.map(([v, l]) => (
            <div key={l} className="bg-brand-ink/80 p-4 text-center">
              <dt className="font-display text-2xl font-black text-brand-lime">{v}</dt>
              <dd className="mt-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-neutral-300">{l}</dd>
            </div>
          ))}
        </dl>
      </PageHeader>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24" data-testid="areas-hub">
        <Reveal className="mb-10"><SectionHead eyebrow="Where we serve" title="Pick your state" /></Reveal>
        <div className="grid gap-4 md:grid-cols-3">
          {Object.keys(STATES).map((k, i) => <StateCard key={k} k={k} i={i} />)}
        </div>
      </section>

      <section className="bg-brand-sand py-20 sm:py-24" data-testid="areas-search-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal className="mb-8 max-w-3xl">
            <SectionHead eyebrow={`All ${AREAS.length} areas`} title="Find your neighborhood">
              <p className="mt-3 text-neutral-600">Searching for a marijuana dispensary near you anywhere in the DMV? Type your neighborhood or town to jump to its page — with directions, the closest Metro and how to find our upstairs door.</p>
            </SectionHead>
          </Reveal>
          <AreaSearch />
          <div className="mt-8 flex flex-wrap gap-3">
            {Object.values(STATES).map((s) => (
              <Link key={s.key} to={`/areas-we-serve/${s.key}`} className="btn-outline !px-5 !py-2.5 text-xs" data-testid={`all-${s.key}-link`}>All {s.short} areas</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
        <Reveal className="mb-10"><SectionHead eyebrow="Getting here" title="How to reach District Gaslight" /></Reveal>
        <GettingHere />
        <div className="mt-10 grid gap-10 lg:grid-cols-2">
          <MapFrame className="h-[360px] lg:h-full lg:min-h-[420px]" />
          <div>
            <SectionHead eyebrow="Store hours" title="Open every day" />
            <div className="mt-6"><HoursList /></div>
            <p className="mt-6 text-sm text-neutral-600">Visiting from out of state? <a href={BUSINESS.nonDcCardUrl} target="_blank" rel="noopener noreferrer" className="font-bold text-brand-red">Register with ABCA's non-DC form</a> before you come.</p>
          </div>
        </div>
      </section>

      <section className="border-t border-neutral-200 bg-white py-20" data-testid="areas-faq">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-5">
          <SectionHead eyebrow="Questions" title="Dispensary near me FAQ" />
          <div className="lg:col-span-3 lg:col-start-3"><FaqAccordion items={FAQS} /></div>
        </div>
      </section>
      <AreaCta where="areas-hub" />
    </>
  );
}
